import React, { useState, useMemo, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { PricingEvent } from "@/store/eventsSlice";
import { EventsFilters } from "./EventsFilters";
import { ChangeType } from "@/utils/eventLabels";
import EventsListCard from "./EventsListCard";
import { ScrapbookEntry } from "@/types/scrapbook";
import EventViewCard from "@/components/company/EventViewCard";
import EventSwipeCard from "@/components/company/EventSwipeCard";
import { LayoutGrid, List, ArrowLeft, ArrowRight, CheckCircle, AlertTriangle, Check, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";
import { checkAdminAuthorization } from "@/utils/authHelpers";
import { addQuarterlyEvent, versionToQuarter, fetchQuarterlyEvents } from "@/utils/quarterlyEventsHelper";
import { toast } from "sonner";
import { useAppSelector } from "@/hooks/useAppSelector";
import ReportErrorDialog from "@/components/company/ReportErrorDialog";
import ConditionalApprovalDialog from "./ConditionalApprovalDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type SortOption = 'quarter-desc' | 'name-asc';

interface EventsListProps {
  events: PricingEvent[];
  swipefileEntries: ScrapbookEntry[];
  onToggleEvent: (event: PricingEvent) => Promise<void>;
  initialFilters?: {
    eventTypes: ChangeType[];
    quarters: string[];
    categories: string[];
  };
  onFilterChange?: (filterType: 'eventTypes' | 'quarters' | 'categories', values: string[]) => void;
  onExportCSV: (filteredEvents: PricingEvent[]) => void;
}

const isEventApproved = (event: PricingEvent, quarterlyEvents: any[]) => {
  const quarter = versionToQuarter(event.v1);
  return quarterlyEvents.some(qe =>
    qe.slug === event.slug &&
    qe.quarter === quarter &&
    qe.v1 === event.v1 &&
    qe.v2 === event.v2
  );
};

const getQuarterlyEventData = (event: PricingEvent, quarterlyEvents: any[]) => {
  const quarter = versionToQuarter(event.v1);
  return quarterlyEvents.find(qe =>
    qe.slug === event.slug &&
    qe.quarter === quarter &&
    qe.v1 === event.v1 &&
    qe.v2 === event.v2
  );
};

export function EventsList({
  events,
  swipefileEntries,
  onToggleEvent,
  initialFilters,
  onFilterChange,
  onExportCSV
}: EventsListProps) {
  const [selectedEventTypes, setSelectedEventTypes] = useState<ChangeType[]>(
    initialFilters?.eventTypes || ['All']
  );
  const [selectedQuarters, setSelectedQuarters] = useState<string[]>(
    initialFilters?.quarters || ['All']
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    initialFilters?.categories || ['All']
  );
  const [sortBy, setSortBy] = useState<SortOption>('quarter-desc');
  const [selectedEvent, setSelectedEvent] = useState<PricingEvent | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isConditionalApproveOpen, setIsConditionalApproveOpen] = useState(false);
  const navigate = useNavigate();

  const approvalFilter = useAppSelector(state => state.events.approvalFilter);
  const quarterlyEvents = useAppSelector(state => state.quarterlyEvents.events);
  const pages = useAppSelector(state => state.supaPages.pages);
  const companies = useAppSelector(state => state.supaCompanies.companies);

  // Check if user is admin on component mount
  useEffect(() => {
    const checkAuth = async () => {
      await checkAdminAuthorization(navigate, (adminStatus) => {
        setIsAdmin(adminStatus);
      });
    };
    checkAuth();
  }, [navigate]);

  // Filter out events with errors and apply other filters
  const sortedAndFilteredEvents = useMemo(() => {
    const validEvents = events;
    console.log('🔍 [EventsList] Total events:', events.length);

    // Add company logos to events if missing
    const eventsWithLogos = validEvents.map(event => {
      if (!event.logoUrl) {
        const company = companies.find(c => c.slug === event.slug);
        return {
          ...event,
          logoUrl: company?.logoUrl || ''
        };
      }
      return event;
    });
    
    const filteredEvents = eventsWithLogos.filter(event => {
      // First check if we should show or hide events with errors
      if (approvalFilter !== 'Error' && event.error) {
        return false;
      }

      // Get approved event data if exists
      const approvedEvent = getQuarterlyEventData(event, quarterlyEvents);

      // Filter out events with no changes
      const hasAnyChanges = approvedEvent ? (
        approvedEvent.plans_changed ||
        approvedEvent.price_increased ||
        approvedEvent.price_decreased ||
        approvedEvent.price_availability_changed ||
        approvedEvent.intro_discount ||
        approvedEvent.display_changed
      ) : (
        event.plansChanged ||
        event.priceIncreased ||
        event.priceDecreased ||
        event.priceAvailbilityChanged ||
        event.introDiscount ||
        event.displayChanged
      );

      if (!hasAnyChanges) {
        return false;
      }

      let eventTypeMatch = true;
      if (!selectedEventTypes.includes('All')) {
        eventTypeMatch = selectedEventTypes.some(type => {
          if (approvedEvent) {
            switch (type) {
              case 'plans':
                return approvedEvent.plans_changed;
              case 'priceIncrease':
                return approvedEvent.price_increased;
              case 'priceDecrease':
                return approvedEvent.price_decreased;
              case 'availability':
                return approvedEvent.price_availability_changed;
              case 'introDiscount':
                return approvedEvent.intro_discount;
              case 'display':
                return approvedEvent.display_changed;
              default:
                return false;
            }
          } else {
            switch (type) {
              case 'plans':
                return event.plansChanged;
              case 'priceIncrease':
                return event.priceIncreased;
              case 'priceDecrease':
                return event.priceDecreased;
              case 'availability':
                return event.priceAvailbilityChanged;
              case 'introDiscount':
                return event.introDiscount;
              case 'display':
                return event.displayChanged;
              default:
                return false;
            }
          }
        });
      }

      let quarterMatch = true;
      if (!selectedQuarters.includes('All')) {
        const year = event.v1.substring(0, 4);
        const month = parseInt(event.v1.substring(4, 6));
        const quarter = `Q${Math.ceil(month / 3)} ${year}`;
        quarterMatch = selectedQuarters.includes(quarter);
      }

      let categoryMatch = true;
      if (!selectedCategories.includes('All')) {
        const company = pages.find(page => page.slug === event.slug);
        categoryMatch = company ? selectedCategories.includes(company.category) : false;
      }

      // Apply approval and error filter
      let approvalMatch = true;
      if (approvalFilter !== 'All') {
        if (approvalFilter === 'Error') {
          approvalMatch = !!event.error;
        } else {
          const quarter = versionToQuarter(event.v1);
          const isApproved = quarterlyEvents.some(qe =>
            qe.slug === event.slug &&
            qe.quarter === quarter &&
            qe.v1 === event.v1 &&
            qe.v2 === event.v2
          );
          approvalMatch = approvalFilter === 'Approved' ? isApproved : !isApproved;
        }
      }

      return eventTypeMatch && quarterMatch && categoryMatch && approvalMatch;
    });

    return filteredEvents.sort((a, b) => {
      if (sortBy === 'quarter-desc') {
        return b.v1.localeCompare(a.v1);
      } else {
        return a.slug.localeCompare(b.slug);
      }
    });
  }, [events, selectedEventTypes, selectedQuarters, selectedCategories, sortBy, approvalFilter, quarterlyEvents, pages]);

  const handleEventTypeChange = (value: string | string[]) => {
    const newValues = Array.isArray(value) ? value as ChangeType[] : [value as ChangeType];
    setSelectedEventTypes(newValues);
    onFilterChange?.('eventTypes', newValues);
  };

  const handleQuarterChange = (value: string | string[]) => {
    const newValues = Array.isArray(value) ? value : [value];
    setSelectedQuarters(newValues);
    onFilterChange?.('quarters', newValues);
  };

  const handleCategoryChange = (value: string | string[]) => {
    const newValues = Array.isArray(value) ? value : [value];
    setSelectedCategories(newValues);
    onFilterChange?.('categories', newValues);
  };

  const handleRemoveFilter = (type: 'eventType' | 'quarter' | 'category', valueToRemove?: string) => {
    if (type === 'eventType') {
      if (valueToRemove) {
        const newValues = selectedEventTypes.filter(type => type !== valueToRemove);
        setSelectedEventTypes(newValues);
        onFilterChange?.('eventTypes', newValues);
      } else {
        setSelectedEventTypes(['All']);
        onFilterChange?.('eventTypes', ['All']);
      }
    } else if (type === 'quarter') {
      if (valueToRemove) {
        const newValues = selectedQuarters.filter(q => q !== valueToRemove);
        setSelectedQuarters(newValues);
        onFilterChange?.('quarters', newValues);
      } else {
        setSelectedQuarters(['All']);
        onFilterChange?.('quarters', ['All']);
      }
    } else if (type === 'category') {
      if (valueToRemove) {
        const newValues = selectedCategories.filter(c => c !== valueToRemove);
        setSelectedCategories(newValues);
        onFilterChange?.('categories', newValues);
      } else {
        setSelectedCategories(['All']);
        onFilterChange?.('categories', ['All']);
      }
    }
  };

  const mapQValues = (qvalues: string[]) => {
    if (!selectedQuarters || selectedQuarters.length === 0) return null;
    if (selectedQuarters[0] == "All") return null;

    const retValue = [];
    for (let i = 0; i < selectedQuarters.length; i++) {
      const [quarter, year] = selectedQuarters[i].split(' ');
      retValue.push(year + quarter);
    }

    return retValue;
  }

  const handleRefresh = async () => {
    console.log('🔄 [EventsList] Refreshing events...');
    await fetchQuarterlyEvents(mapQValues(selectedQuarters), selectedEventTypes);
    console.log(quarterlyEvents);
    toast.success('Events refreshed successfully');
  };

  const handleNavigate = (direction: 'next' | 'prev') => {
    if (!selectedEvent) return;

    const currentIndex = sortedAndFilteredEvents.findIndex(
      event => event.slug === selectedEvent.slug &&
               event.v1 === selectedEvent.v1 &&
               event.v2 === selectedEvent.v2
    );

    if (currentIndex === -1) return;

    const newIndex = direction === 'next'
      ? Math.min(currentIndex + 1, sortedAndFilteredEvents.length - 1)
      : Math.max(currentIndex - 1, 0);

    setSelectedEvent(sortedAndFilteredEvents[newIndex]);
  };

  const handleViewChange = (mode: 'list' | 'detail', event?: PricingEvent) => {
    setViewMode(mode);
    if (mode === 'detail') {
      setSelectedEvent(event || (sortedAndFilteredEvents.length > 0 ? sortedAndFilteredEvents[0] : null));
    }
  };

  const handleApproveEvent = async () => {
    if (!selectedEvent) return;

    try {
      console.log('🔄 [EventsList] Approving event:', selectedEvent.slug);
      const quarter = versionToQuarter(selectedEvent.v1);

      await addQuarterlyEvent(
        selectedEvent.slug,
        quarter,
        {
          v1: selectedEvent.v1,
          v2: selectedEvent.v2,
          has_changes: selectedEvent.hasChanges,
          plans_changed: selectedEvent.plansChanged,
          price_increased: selectedEvent.priceIncreased,
          price_decreased: selectedEvent.priceDecreased,
          price_availability_changed: selectedEvent.priceAvailbilityChanged,
          display_changed: selectedEvent.displayChanged,
          intro_discount: selectedEvent.introDiscount,
          changes: selectedEvent.changes,
          v1_url: selectedEvent.beforeUrl,
          v2_url: selectedEvent.afterUrl,
          v1_pi_url: selectedEvent.beforeUrl || '',
          v2_pi_url: selectedEvent.afterUrl || ''
        },
        true // force update if exists
      );

      toast.success('Event approved successfully');

      // Move to next event if available
      handleNavigate('next');
    } catch (error) {
      console.error('❌ [EventsList] Error approving event:', error);
      toast.error('Failed to approve event');
    }
  };

  const openConditionalApprove = (isEditing: boolean = false) => {
    if (!selectedEvent) return;

    // If editing an approved event, use those values
    const approvedEvent = isEditing ? getQuarterlyEventData(selectedEvent, quarterlyEvents) : null;

    console.log('🔄 [EventsList] Opening conditional approve dialog with event:', selectedEvent);
    console.log('🔄 [EventsList] Approved event data:', approvedEvent);

    const initialFlags = {
      plansChanged: approvedEvent ? approvedEvent.plans_changed : (selectedEvent.plansChanged || false),
      priceIncreased: approvedEvent ? approvedEvent.price_increased : (selectedEvent.priceIncreased || false),
      priceDecreased: approvedEvent ? approvedEvent.price_decreased : (selectedEvent.priceDecreased || false),
      priceAvailbilityChanged: approvedEvent ? approvedEvent.price_availability_changed : (selectedEvent.priceAvailbilityChanged || false),
      introDiscount: approvedEvent ? approvedEvent.intro_discount : (selectedEvent.introDiscount || false),
      displayChanged: approvedEvent ? approvedEvent.display_changed : (selectedEvent.displayChanged || false)
    };

    console.log('🔄 [EventsList] Setting initial flags:', initialFlags);

    // Set the event for the dialog
    setSelectedEvent(selectedEvent);
    setIsConditionalApproveOpen(true);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedEvent(null);
  };

  const handleErrorReportSubmitted = () => {
    setIsReportModalOpen(false);
    handleNavigate('next');
  };

  const storeScrapbookEntries = []; // Project functionality removed

  const isEventInSwipefile = (event: PricingEvent) => {
    const value = storeScrapbookEntries.some(entry =>
      entry.slug === event.slug &&
      entry.before_version === event.v1 &&
      entry.after_version === event.v2
    );

    return value;
  };

  if (viewMode === 'detail' && selectedEvent) {
    const approved = isEventApproved(selectedEvent, quarterlyEvents);
    const approvedEvent = approved ? getQuarterlyEventData(selectedEvent, quarterlyEvents) : null;

    const eventToDisplay = approvedEvent ? {
      ...selectedEvent,
      plansChanged: approvedEvent.plans_changed,
      priceIncreased: approvedEvent.price_increased,
      priceDecreased: approvedEvent.price_decreased,
      priceAvailbilityChanged: approvedEvent.price_availability_changed,
      introDiscount: approvedEvent.intro_discount,
      displayChanged: approvedEvent.display_changed,
      changes: approvedEvent.changes || selectedEvent.changes
    } : selectedEvent;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={handleBackToList}
              className="flex items-center gap-2"
            >
              <List className="h-4 w-4" />
              Back to List
            </Button>
            <div className="flex items-center gap-1 ml-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleNavigate('prev')}
                disabled={sortedAndFilteredEvents.indexOf(selectedEvent) === 0}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleNavigate('next')}
                disabled={sortedAndFilteredEvents.indexOf(selectedEvent) === sortedAndFilteredEvents.length - 1}
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <Button
                  variant={approved ? "secondary" : "outline"}
                  size="sm"
                  className="gap-2"
                  onClick={handleApproveEvent}
                  disabled={approved}
                >
                  <CheckCircle className="h-4 w-4" />
                  {approved ? "Approved" : "Approve"}
                </Button>
                {approved ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => openConditionalApprove(true)}
                  >
                    <Check className="h-4 w-4" />
                    Edit Approval
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => openConditionalApprove(false)}
                  >
                    <Check className="h-4 w-4" />
                    Approve Conditional
                  </Button>
                )}
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setIsReportModalOpen(true)}
            >
              <AlertTriangle className="h-4 w-4" />
              Report Error
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="w-full">
            {swipefileEntries.find(entry =>
              entry.slug === selectedEvent.slug &&
              entry.before_version === selectedEvent.v1 &&
              entry.after_version === selectedEvent.v2
            ) ? (
              <EventSwipeCard
                company={{
                  slug: selectedEvent.slug,
                  logoUrl: selectedEvent.logoUrl,
                  beforeUrl: selectedEvent.beforeUrl,
                  afterUrl: selectedEvent.afterUrl,
                  v1: selectedEvent.v1,
                  v2: selectedEvent.v2,
                  description: selectedEvent.description,
                  hasChanges: true,
                  changes: [],
                  quarter: selectedEvent.v1.substring(4, 6),
                  year: selectedEvent.v1.substring(0, 4),
                  plansChanged: selectedEvent.plansChanged || false,
                  priceIncreased: selectedEvent.priceIncreased || false,
                  priceDecreased: selectedEvent.priceDecreased || false,
                  priceAvailbilityChanged: selectedEvent.priceAvailbilityChanged || false,
                  introDiscount: selectedEvent.introDiscount || false,
                  displayChanged: selectedEvent.displayChanged || false
                }}
                onToggleProject={() => onToggleEvent(selectedEvent)}
                isInProject={true}
                fabricJson={swipefileEntries.find(entry =>
                  entry.slug === selectedEvent.slug &&
                  entry.before_version === selectedEvent.v1 &&
                  entry.after_version === selectedEvent.v2
                )?.fabric_json}
                fabricJson2={swipefileEntries.find(entry =>
                  entry.slug === selectedEvent.slug &&
                  entry.before_version === selectedEvent.v1 &&
                  entry.after_version === selectedEvent.v2
                )?.fabric_json2}
                entryId={swipefileEntries.find(entry =>
                  entry.slug === selectedEvent.slug &&
                  entry.before_version === selectedEvent.v1 &&
                  entry.after_version === selectedEvent.v2
                )?.id}
              />
            ) : (
              <EventViewCard
                company={eventToDisplay}
                isInSwipefile={isEventInSwipefile(eventToDisplay)}
                onToggle={(e) => {
                  e.stopPropagation();
                  onToggleEvent(eventToDisplay);
                }}
              />
            )}
          </div>
        </div>

        <ReportErrorDialog
          isOpen={isReportModalOpen}
          onOpenChange={setIsReportModalOpen}
          company={eventToDisplay}
          existingError={eventToDisplay.error}
          onSubmitted={handleErrorReportSubmitted}
        />

        <ConditionalApprovalDialog
          isOpen={isConditionalApproveOpen}
          onOpenChange={setIsConditionalApproveOpen}
          event={selectedEvent}
          isEditing={approved}
          initialFlags={approvedEvent ? {
            plansChanged: approvedEvent.plans_changed,
            priceIncreased: approvedEvent.price_increased,
            priceDecreased: approvedEvent.price_decreased,
            priceAvailbilityChanged: approvedEvent.price_availability_changed,
            introDiscount: approvedEvent.intro_discount,
            displayChanged: approvedEvent.display_changed
          } : selectedEvent}
          onSuccess={() => handleNavigate('next')}
        />
      </div>
    );

  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <EventsFilters
          events={events}
          selectedEventTypes={selectedEventTypes}
          selectedQuarters={selectedQuarters}
          selectedCategories={selectedCategories}
          onEventTypeChange={handleEventTypeChange}
          onQuarterChange={handleQuarterChange}
          onCategoryChange={handleCategoryChange}
          onRemoveFilter={handleRemoveFilter}
          onRefresh={handleRefresh}
        />
        <div className="flex flex-col items-end gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExportCSV(sortedAndFilteredEvents)}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          <div className="flex items-center gap-2">
            <Label>Sort by</Label>
            <Select
              value={sortBy}
              onValueChange={(value: SortOption) => setSortBy(value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="quarter-desc">Newest to Oldest</SelectItem>
                <SelectItem value="name-asc">Company Name (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Separator className="my-4" />

      <div className="p-4 bg-white rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <Label>Events List ({sortedAndFilteredEvents.length} valid events found)</Label>
        </div>
        <div className="space-y-4">
          {viewMode === 'list' ? (
            sortedAndFilteredEvents.map((event) => {
              const approved = isEventApproved(event, quarterlyEvents);
              const approvedEvent = approved ? getQuarterlyEventData(event, quarterlyEvents) : null;

              const eventToDisplay = approvedEvent ? {
                ...event,
                plansChanged: approvedEvent.plans_changed,
                priceIncreased: approvedEvent.price_increased,
                priceDecreased: approvedEvent.price_decreased,
                priceAvailbilityChanged: approvedEvent.price_availability_changed,
                introDiscount: approvedEvent.intro_discount,
                displayChanged: approvedEvent.display_changed,
                changes: approvedEvent.changes || event.changes
              } : event;

              return (
                <EventsListCard
                  key={`${event.slug}-${event.v1}-${event.v2}`}
                  event={eventToDisplay}
                  swipefileEntry={swipefileEntries.find(entry =>
                    entry.slug === event.slug &&
                    entry.before_version === event.v1 &&
                    entry.after_version === event.v2
                  )}
                  onToggle={() => onToggleEvent(event)}
                  onClick={() => {
                    setSelectedEvent(event);
                    handleViewChange('detail', event);
                  }}
                />
              );
            })
          ) : null}
        </div>
      </div>
    </div>
  );
}
